import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Fab,
  Paper,
  Box,
  Typography,
  IconButton,
  Button,
  Slide,
  InputBase,
  Link as MuiLink,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { useSessionState } from 'context/session/session';

// ─── Types ───────────────────────────────────────────────────────────────────

type QuickOption = {
  label: string;
  emoji?: string;
  actionId: string;
};

type MessageLink = {
  text: string;
  href: string;
};

type Message = {
  id: string;
  from: 'bot' | 'user';
  text: string;
  options?: QuickOption[];
  links?: MessageLink[];
};

type InputMode = null | 'bug_description' | 'improvement_description';

// ─── Constants ───────────────────────────────────────────────────────────────

const MENU_OPTION: QuickOption = { label: 'Menu principal', emoji: '🏠', actionId: 'menu' };

// ─── Render bot text with links ─────────────────────────────────────────────

function BotMessageContent({ text, links }: { text: string; links?: MessageLink[] }) {
  if (!links || links.length === 0) {
    return <>{text}</>;
  }

  const parts: React.ReactNode[] = [];
  let remaining = text;
  let keyIndex = 0;

  for (const link of links) {
    const placeholder = `[${link.text}]`;
    const idx = remaining.indexOf(placeholder);
    if (idx === -1) continue;

    if (idx > 0) {
      parts.push(<span key={keyIndex++}>{remaining.substring(0, idx)}</span>);
    }
    parts.push(
      <NextLink key={keyIndex++} href={link.href} passHref legacyBehavior>
        <MuiLink
          sx={{
            color: 'primary.main',
            fontWeight: 600,
            textDecoration: 'underline',
            cursor: 'pointer',
            '&:hover': { color: 'secondary.main' },
          }}
        >
          {link.text}
        </MuiLink>
      </NextLink>,
    );
    remaining = remaining.substring(idx + placeholder.length);
  }

  if (remaining) {
    parts.push(<span key={keyIndex++}>{remaining}</span>);
  }

  return <>{parts}</>;
}

// ─── Contextual suggestions by page ─────────────────────────────────────────

function getContextualSuggestion(path: string): { greeting: string; options: QuickOption[] } | null {
  if (path === '/signin' || path === '/signin/') {
    return {
      greeting: 'Vous êtes sur la page de connexion. Besoin d\'aide ?',
      options: [
        { label: 'Créer un compte', emoji: '📝', actionId: 'create_account' },
        { label: 'Mot de passe oublié', emoji: '🔑', actionId: 'go_forgot_password' },
        { label: 'Email oublié', emoji: '📧', actionId: 'lost_email' },
        MENU_OPTION,
      ],
    };
  }
  if (path === '/agenda' || path === '/agenda/') {
    return {
      greeting: 'Vous êtes sur l\'agenda. Besoin d\'aide pour trouver un événement ?',
      options: [
        { label: 'Aide filtres', emoji: '🔍', actionId: 'search_event_tips' },
        MENU_OPTION,
      ],
    };
  }
  if (path.startsWith('/annuaire')) {
    return {
      greeting: 'Parcourez les catégories pour trouver des acteurs de la transition.',
      options: [
        { label: 'Voir la carte', emoji: '🗺️', actionId: 'search_map' },
        MENU_OPTION,
      ],
    };
  }
  if (path.startsWith('/addactor')) {
    return {
      greeting: 'Besoin d\'aide pour créer votre page acteur ?',
      options: [
        { label: 'Conseils', emoji: '💡', actionId: 'tips_actor' },
        MENU_OPTION,
      ],
    };
  }
  if (path.startsWith('/admin/events/add')) {
    return {
      greeting: 'Besoin d\'aide pour créer votre événement ?',
      options: [
        { label: 'Inviter un acteur', emoji: '✉️', actionId: 'collab_invite' },
        MENU_OPTION,
      ],
    };
  }
  if (path.startsWith('/admin/events/')) {
    return {
      greeting: 'Vous modifiez un événement. Besoin d\'aide ?',
      options: [
        { label: 'Inviter un acteur', emoji: '✉️', actionId: 'collab_invite' },
        { label: 'Conseils', emoji: '💡', actionId: 'tips_event' },
        MENU_OPTION,
      ],
    };
  }
  if (path.startsWith('/admin/actors/')) {
    return {
      greeting: 'Vous modifiez votre page acteur. Besoin d\'aide ?',
      options: [
        { label: 'Conseils', emoji: '💡', actionId: 'tips_actor' },
        MENU_OPTION,
      ],
    };
  }
  if (path === '/signup' || path === '/signup/') {
    return {
      greeting: 'Remplissez le formulaire pour créer votre compte OUAAA!.',
      options: [
        { label: 'Déjà un compte ?', emoji: '🔑', actionId: 'go_signin' },
        MENU_OPTION,
      ],
    };
  }
  if (path === '/carte' || path === '/carte/') {
    return {
      greeting: 'Vous êtes sur la carte interactive. Besoin d\'aide ?',
      options: [
        { label: 'Aide filtres carte', emoji: '🗺️', actionId: 'filters_map' },
        MENU_OPTION,
      ],
    };
  }
  if (path === '/recettes' || path === '/recettes/') {
    return {
      greeting: 'Vous êtes sur les recettes végétariennes. Envie de cuisiner ?',
      options: [
        { label: 'Créer une recette', emoji: '🥗', actionId: 'create_recipe' },
        { label: 'Impact carbone', emoji: '🌍', actionId: 'recipe_carbon' },
        MENU_OPTION,
      ],
    };
  }
  if (path.startsWith('/recette/')) {
    return {
      greeting: 'Vous consultez une recette. Besoin d\'informations ?',
      options: [
        { label: 'Impact carbone', emoji: '🌍', actionId: 'recipe_carbon' },
        { label: 'Toutes les recettes', emoji: '📖', actionId: 'search_recipe' },
        MENU_OPTION,
      ],
    };
  }
  if (path.startsWith('/admin/recipes')) {
    return {
      greeting: 'Besoin d\'aide pour gérer vos recettes ?',
      options: [
        { label: 'Créer une recette', emoji: '🥗', actionId: 'create_recipe' },
        { label: 'Impact carbone', emoji: '🌍', actionId: 'recipe_carbon' },
        MENU_OPTION,
      ],
    };
  }
  if (path === '/news' || path === '/news/') {
    return {
      greeting: 'Vous êtes sur les actualités OUAAA!.',
      options: [
        { label: 'Créer un article', emoji: '📰', actionId: 'create_article' },
        MENU_OPTION,
      ],
    };
  }
  if (path.startsWith('/article/')) {
    return {
      greeting: 'Vous lisez un article. Besoin d\'aide ?',
      options: [
        { label: 'Tous les articles', emoji: '📰', actionId: 'search_article' },
        MENU_OPTION,
      ],
    };
  }
  return null;
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function ChatbotWidget() {
  const router = useRouter();
  const user = useSessionState();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMode, setInputMode] = useState<InputMode>(null);
  const [inputValue, setInputValue] = useState('');
  const [showPulse, setShowPulse] = useState(true);
  const [isHidden, setIsHidden] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragPos, setDragPos] = useState<{ x: number; y: number } | null>(null);
  const [isOverDismiss, setIsOverDismiss] = useState(false);
  const [showHideSnackbar, setShowHideSnackbar] = useState(false);

  const isAdminPage = router.asPath.startsWith('/admin') || router.asPath.startsWith('/addactor');

  const navigationHistory = useRef<{ path: string; time: string }[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const initialized = useRef(false);
  const dragStateRef = useRef<{ startX: number; startY: number; isDragging: boolean; isOverDismiss: boolean } | null>(null);

  // ── Track navigation history (for bug reports) ──

  useEffect(() => {
    if (navigationHistory.current.length === 0) {
      navigationHistory.current.push({
        path: router.asPath,
        time: new Date().toLocaleTimeString('fr-FR'),
      });
    }

    const handleRouteChange = (url: string) => {
      navigationHistory.current.push({
        path: url,
        time: new Date().toLocaleTimeString('fr-FR'),
      });
      if (navigationHistory.current.length > 30) {
        navigationHistory.current = navigationHistory.current.slice(-30);
      }
    };

    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router]);

  // ── Auto-scroll messages ──

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Restore hidden state ──

  useEffect(() => {
    const sync = () => {
      try {
        if (user?.id) {
          setIsHidden(localStorage.getItem(`chatbot_hidden_${user.id}`) === 'true');
        } else {
          setIsHidden(sessionStorage.getItem('chatbot_hidden') === 'true');
        }
      } catch { }
    };
    sync();
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, [user?.id]);

  // ── Helpers ──

  const addBotMessage = useCallback((text: string, options?: QuickOption[], links?: MessageLink[]) => {
    setMessages((prev) => [
      ...prev,
      {
        id: `bot-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        from: 'bot' as const,
        text,
        options,
        links,
      },
    ]);
  }, []);

  const addUserMessage = useCallback((text: string) => {
    setMessages((prev) => [
      ...prev,
      {
        id: `user-${Date.now()}`,
        from: 'user' as const,
        text,
      },
    ]);
  }, []);

  const showMainMenu = useCallback(() => {
    addBotMessage('Comment puis-je vous aider ?', [
      { label: 'Rechercher', emoji: '🔍', actionId: 'search' },
      { label: 'Créer', emoji: '➕', actionId: 'create' },
      { label: 'Modifier', emoji: '✏️', actionId: 'modify' },
      { label: 'Filtres & carte', emoji: '🗺️', actionId: 'filters' },
      { label: 'Recettes', emoji: '🥗', actionId: 'recipes' },
      { label: 'Articles', emoji: '📰', actionId: 'articles' },
      { label: 'Notifications', emoji: '🔔', actionId: 'notifications' },
      { label: 'Collaborer', emoji: '🤝', actionId: 'collaborate' },
      { label: 'Mon compte', emoji: '👤', actionId: 'account' },
      { label: 'FAQ', emoji: '❓', actionId: 'faq' },
      { label: 'Signaler un bug', emoji: '🐛', actionId: 'bug' },
      { label: 'Amélioration', emoji: '💡', actionId: 'improvement' },
      { label: 'Support', emoji: '💬', actionId: 'contact' },
      { label: 'Masquer l\'assistant', emoji: '👋', actionId: 'hide_chatbot' },
    ]);
  }, [addBotMessage]);

  const getSessionInfo = useCallback(
    () => ({
      pageActuelle: typeof window !== 'undefined' ? window.location.href : router.asPath,
      navigateur: typeof navigator !== 'undefined' ? navigator.userAgent : 'N/A',
      ecran: typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : 'N/A',
      utilisateur: user ? `${user.surname} ${user.lastname} (${user.email})` : 'Non connecté',
      historique: navigationHistory.current.map((h) => `${h.time} - ${h.path}`).join('\n'),
      date: new Date().toLocaleString('fr-FR'),
    }),
    [router.asPath, user],
  );

  const hideChatbot = useCallback(() => {
    setIsOpen(false);
    setIsHidden(true);
    try {
      if (user?.id) {
        localStorage.setItem(`chatbot_hidden_${user.id}`, 'true');
      } else {
        sessionStorage.setItem('chatbot_hidden', 'true');
      }
    } catch { }
    setShowHideSnackbar(true);
  }, [user]);

  const showChatbot = useCallback(() => {
    setIsHidden(false);
    try {
      if (user?.id) {
        localStorage.removeItem(`chatbot_hidden_${user.id}`);
      } else {
        sessionStorage.removeItem('chatbot_hidden');
      }
    } catch { }
  }, [user]);

  // ── Action handler ──

  const handleAction = useCallback(
    (actionId: string) => {
      switch (actionId) {
        // ── MENU ──
        case 'menu':
          showMainMenu();
          break;

        // ── SEARCH ──
        case 'search':
          addBotMessage('Que souhaitez-vous rechercher ?', [
            { label: 'Un acteur', emoji: '👤', actionId: 'search_actor' },
            { label: 'Un événement', emoji: '📅', actionId: 'search_event' },
            { label: 'Sur la carte', emoji: '🗺️', actionId: 'search_map' },
            { label: 'Une recette', emoji: '🥗', actionId: 'search_recipe' },
            { label: 'Un article', emoji: '📰', actionId: 'search_article' },
            MENU_OPTION,
          ]);
          break;

        case 'search_actor':
          addUserMessage('Rechercher un acteur');
          addBotMessage(
            '📂 Rendez-vous sur [l\'annuaire] pour parcourir les acteurs par catégorie.\n\nVous pouvez aussi utiliser [la carte] pour une recherche géographique.',
            [MENU_OPTION],
            [
              { text: 'l\'annuaire', href: '/annuaire' },
              { text: 'la carte', href: '/carte' },
            ],
          );
          break;

        case 'search_event':
          addUserMessage('Rechercher un événement');
          addBotMessage(
            '📅 Rendez-vous sur [l\'agenda] pour trouver un événement.\n\n' +
            'Utilisez les filtres pour affiner :\n' +
            '• 📅 Date : choisissez une date de début\n' +
            '• 📍 Lieu : indiquez une ville et un rayon\n' +
            '• 🏷️ Thème : sélectionnez des catégories\n' +
            '• 🔤 Recherche : tapez un mot-clé',
            [MENU_OPTION],
            [{ text: 'l\'agenda', href: '/agenda' }],
          );
          break;

        case 'search_event_tips':
          addBotMessage(
            '🔍 Pour trouver un événement sur [l\'agenda] :\n\n' +
            '1. 📅 Cliquez sur le calendrier pour choisir une date\n' +
            '2. 📍 Tapez une ville (ex: "La Rochelle") et réglez le rayon\n' +
            '3. 🏷️ Ouvrez les filtres par catégorie pour affiner\n' +
            '4. 🔄 Activez le filtre "événements récurrents" si besoin\n\n' +
            'Les résultats se mettent à jour automatiquement !',
            [MENU_OPTION],
            [{ text: 'l\'agenda', href: '/agenda' }],
          );
          break;

        case 'search_map':
          addUserMessage('Voir la carte');
          addBotMessage(
            '🗺️ Rendez-vous sur [la carte] pour voir tous les acteurs et événements géolocalisés.\nZoomez et cliquez sur les marqueurs pour plus d\'informations !',
            [MENU_OPTION],
            [{ text: 'la carte', href: '/carte' }],
          );
          break;

        case 'search_recipe':
          addUserMessage('Rechercher une recette');
          addBotMessage(
            '🥗 Rendez-vous sur [les recettes] pour parcourir les recettes végétariennes de la communauté.\n\n' +
            'Chaque recette affiche son impact carbone pour vous aider à cuisiner de manière responsable !',
            [MENU_OPTION],
            [{ text: 'les recettes', href: '/recettes' }],
          );
          break;

        case 'search_article':
          addUserMessage('Rechercher un article');
          addBotMessage(
            '📰 Retrouvez tous les articles et actualités sur [la page actualités].\n\n' +
            'Découvrez les dernières nouvelles de la transition écologique et sociale en Aunis !',
            [MENU_OPTION],
            [{ text: 'la page actualités', href: '/news' }],
          );
          break;

        // ── CREATE ──
        case 'create':
          addBotMessage('Que souhaitez-vous créer ?', [
            { label: 'Un compte', emoji: '👤', actionId: 'create_account' },
            { label: 'Une page acteur', emoji: '🏢', actionId: 'create_actor' },
            { label: 'Un événement', emoji: '📅', actionId: 'create_event' },
            { label: 'Une recette', emoji: '🥗', actionId: 'create_recipe' },
            { label: 'Un article', emoji: '📰', actionId: 'create_article' },
            MENU_OPTION,
          ]);
          break;

        case 'create_account':
          addUserMessage('Créer un compte');
          addBotMessage(
            '📝 Rendez-vous sur la page [Créer un compte] pour vous inscrire.\nUne fois votre compte créé, vous pourrez créer des pages acteur et des événements !',
            [MENU_OPTION],
            [{ text: 'Créer un compte', href: '/signup' }],
          );
          break;

        case 'create_actor':
          addUserMessage('Créer une page acteur');
          if (user) {
            addBotMessage(
              '🏢 Rendez-vous sur [Créer une page acteur] pour référencer votre structure.\n\n' +
              'Conseils :\n' +
              '• Ajoutez un logo et une photo de couverture\n' +
              '• Rédigez une description claire\n' +
              '• Indiquez votre adresse pour apparaître sur la carte\n' +
              '• Ajoutez vos réseaux sociaux',
              [MENU_OPTION],
              [{ text: 'Créer une page acteur', href: '/addactor' }],
            );
          } else {
            addBotMessage('⚠️ Vous devez être connecté pour créer une page acteur.', [
              { label: 'Se connecter', emoji: '🔑', actionId: 'go_signin' },
              { label: 'Créer un compte', emoji: '📝', actionId: 'create_account' },
              MENU_OPTION,
            ]);
          }
          break;

        case 'create_event':
          addUserMessage('Créer un événement');
          if (user) {
            addBotMessage(
              '📅 Rendez-vous sur [Créer un événement] pour remplir le formulaire.\n\n' +
              'Indiquez le titre, la date, le lieu et une description.\n' +
              'Vous pouvez aussi inviter d\'autres structures à co-organiser !',
              [MENU_OPTION],
              [{ text: 'Créer un événement', href: '/admin/events/add' }],
            );
          } else {
            addBotMessage('⚠️ Vous devez être connecté pour créer un événement.', [
              { label: 'Se connecter', emoji: '🔑', actionId: 'go_signin' },
              { label: 'Créer un compte', emoji: '📝', actionId: 'create_account' },
              MENU_OPTION,
            ]);
          }
          break;

        case 'create_recipe':
          addUserMessage('Créer une recette');
          if (user) {
            addBotMessage(
              '🥗 Rendez-vous sur [Créer une recette] pour partager votre recette végétarienne.\n\n' +
              'Conseils :\n' +
              '• Ajoutez les ingrédients avec leurs quantités\n' +
              '• L\'impact carbone est calculé automatiquement à partir de la base nutritionnelle\n' +
              '• Ajoutez une belle photo pour donner envie\n' +
              '• Précisez le temps de préparation et le nombre de portions',
              [{ label: 'Impact carbone ?', emoji: '🌍', actionId: 'recipe_carbon' }, MENU_OPTION],
              [{ text: 'Créer une recette', href: '/admin/recipes/add' }],
            );
          } else {
            addBotMessage('⚠️ Vous devez être connecté pour créer une recette.', [
              { label: 'Se connecter', emoji: '🔑', actionId: 'go_signin' },
              { label: 'Créer un compte', emoji: '📝', actionId: 'create_account' },
              MENU_OPTION,
            ]);
          }
          break;

        case 'create_article':
          addUserMessage('Créer un article');
          if (user) {
            addBotMessage(
              '📰 Rendez-vous sur [Créer un article] pour publier une actualité.\n\n' +
              'Partagez des nouvelles, des retours d\'expérience ou des informations utiles pour la communauté de la transition !',
              [MENU_OPTION],
              [{ text: 'Créer un article', href: '/admin/news/add' }],
            );
          } else {
            addBotMessage('⚠️ Vous devez être connecté pour créer un article.', [
              { label: 'Se connecter', emoji: '🔑', actionId: 'go_signin' },
              { label: 'Créer un compte', emoji: '📝', actionId: 'create_account' },
              MENU_OPTION,
            ]);
          }
          break;

        // ── MODIFY ──
        case 'modify':
          addBotMessage('Que souhaitez-vous modifier ?', [
            { label: 'Ma page acteur', emoji: '🏢', actionId: 'modify_actor' },
            { label: 'Un événement', emoji: '📅', actionId: 'modify_event' },
            { label: 'Une recette', emoji: '🥗', actionId: 'modify_recipe' },
            { label: 'Un article', emoji: '📰', actionId: 'modify_article' },
            MENU_OPTION,
          ]);
          break;

        case 'modify_actor':
          addUserMessage('Modifier ma page acteur');
          if (user) {
            addBotMessage(
              '🏢 Pour modifier votre page acteur :\n\n' +
              '1. Rendez-vous sur [la liste de vos acteurs]\n' +
              '2. Cliquez sur l\'acteur à modifier\n' +
              '3. Modifiez les informations souhaitées\n' +
              '4. Cliquez sur "Enregistrer"\n\n' +
              'Vous pouvez aussi accéder à la modification depuis la page publique de votre acteur en cliquant sur le bouton "Modifier".',
              [MENU_OPTION],
              [{ text: 'la liste de vos acteurs', href: '/admin/actors' }],
            );
          } else {
            addBotMessage('⚠️ Vous devez être connecté pour modifier une page acteur.', [
              { label: 'Se connecter', emoji: '🔑', actionId: 'go_signin' },
              MENU_OPTION,
            ]);
          }
          break;

        case 'modify_event':
          addUserMessage('Modifier un événement');
          if (user) {
            addBotMessage(
              '📅 Pour modifier un événement :\n\n' +
              '1. Rendez-vous sur [la liste de vos événements]\n' +
              '2. Cliquez sur l\'événement à modifier\n' +
              '3. Modifiez les informations souhaitées\n' +
              '4. Cliquez sur "Enregistrer"\n\n' +
              'Vous pouvez aussi accéder à la modification depuis la page publique de l\'événement.',
              [MENU_OPTION],
              [{ text: 'la liste de vos événements', href: '/admin/events' }],
            );
          } else {
            addBotMessage('⚠️ Vous devez être connecté pour modifier un événement.', [
              { label: 'Se connecter', emoji: '🔑', actionId: 'go_signin' },
              MENU_OPTION,
            ]);
          }
          break;

        case 'modify_recipe':
          addUserMessage('Modifier une recette');
          if (user) {
            addBotMessage(
              '🥗 Pour modifier une recette :\n\n' +
              '1. Rendez-vous sur [la liste de vos recettes]\n' +
              '2. Cliquez sur la recette à modifier\n' +
              '3. Modifiez les ingrédients, les étapes ou les photos\n' +
              '4. L\'impact carbone sera recalculé automatiquement\n' +
              '5. Cliquez sur "Enregistrer"',
              [MENU_OPTION],
              [{ text: 'la liste de vos recettes', href: '/admin/recipes' }],
            );
          } else {
            addBotMessage('⚠️ Vous devez être connecté pour modifier une recette.', [
              { label: 'Se connecter', emoji: '🔑', actionId: 'go_signin' },
              MENU_OPTION,
            ]);
          }
          break;

        case 'modify_article':
          addUserMessage('Modifier un article');
          if (user) {
            addBotMessage(
              '📰 Pour modifier un article :\n\n' +
              '1. Rendez-vous sur [la liste de vos articles]\n' +
              '2. Cliquez sur l\'article à modifier\n' +
              '3. Modifiez le contenu souhaité\n' +
              '4. Cliquez sur "Enregistrer"',
              [MENU_OPTION],
              [{ text: 'la liste de vos articles', href: '/admin/news' }],
            );
          } else {
            addBotMessage('⚠️ Vous devez être connecté pour modifier un article.', [
              { label: 'Se connecter', emoji: '🔑', actionId: 'go_signin' },
              MENU_OPTION,
            ]);
          }
          break;

        // ── AUTH ──
        case 'go_signin':
          addUserMessage('Se connecter');
          addBotMessage(
            '🔑 Rendez-vous sur la page [Connexion] pour vous connecter avec votre email et mot de passe.',
            [
              { label: 'Mot de passe oublié', emoji: '❓', actionId: 'go_forgot_password' },
              { label: 'Email oublié', emoji: '📧', actionId: 'lost_email' },
              MENU_OPTION,
            ],
            [{ text: 'Connexion', href: '/signin' }],
          );
          break;

        case 'go_forgot_password':
          addUserMessage('Mot de passe oublié');
          addBotMessage(
            '🔑 Rendez-vous sur [Mot de passe oublié] et entrez votre adresse email.\nVous recevrez un code de réinitialisation par email.',
            [
              { label: 'Email oublié aussi ?', emoji: '📧', actionId: 'lost_email' },
              MENU_OPTION,
            ],
            [{ text: 'Mot de passe oublié', href: '/forgotPassword' }],
          );
          break;

        // ── ACCOUNT ──
        case 'account':
          addBotMessage('Que souhaitez-vous faire avec votre compte ?', [
            { label: 'Mot de passe oublié', emoji: '🔑', actionId: 'go_forgot_password' },
            { label: 'Email oublié', emoji: '📧', actionId: 'lost_email' },
            { label: 'Se connecter', emoji: '🔓', actionId: 'go_signin' },
            { label: 'Créer un compte', emoji: '📝', actionId: 'create_account' },
            MENU_OPTION,
          ]);
          break;

        case 'lost_email':
          addUserMessage('Email oublié');
          addBotMessage(
            '📧 Si vous ne connaissez plus l\'adresse email associée à votre compte ou à votre acteur, contactez le support.\n\n' +
            'Rendez-vous sur le [formulaire de contact] et indiquez :\n' +
            '• Le nom de votre structure\n' +
            '• Votre nom et prénom\n' +
            '• Toute information permettant de vous identifier\n\n' +
            'L\'équipe OUAAA! vous aidera à retrouver votre accès.',
            [
              { label: 'Contacter le support', emoji: '💬', actionId: 'contact' },
              MENU_OPTION,
            ],
            [{ text: 'formulaire de contact', href: '/contact' }],
          );
          break;

        // ── COLLABORATE ──
        case 'collaborate':
          addBotMessage('Comment souhaitez-vous collaborer ?', [
            { label: 'Co-organiser un événement', emoji: '📅', actionId: 'collab_coorg' },
            { label: 'Inviter un acteur', emoji: '✉️', actionId: 'collab_invite' },
            MENU_OPTION,
          ]);
          break;

        case 'collab_coorg':
          addUserMessage('Co-organiser un événement');
          if (user) {
            addBotMessage(
              '🤝 Rendez-vous sur [l\'agenda collaboratif] pour voir les événements ouverts à la co-organisation.\n\n' +
              'Consultez les propositions et cliquez sur un événement pour proposer votre participation !',
              [MENU_OPTION],
              [{ text: 'l\'agenda collaboratif', href: '/agenda-collaboratif' }],
            );
          } else {
            addBotMessage('⚠️ Vous devez être connecté pour co-organiser un événement.', [
              { label: 'Se connecter', emoji: '🔑', actionId: 'go_signin' },
              { label: 'Créer un compte', emoji: '📝', actionId: 'create_account' },
              MENU_OPTION,
            ]);
          }
          break;

        case 'collab_invite':
          addUserMessage('Inviter un acteur');
          addBotMessage(
            '✉️ Lors de la création ou modification d\'un événement, vous pouvez inviter un nouvel acteur.\n\n' +
            'Dans le formulaire, remplissez :\n' +
            '• "Nom de la structure à inviter"\n' +
            '• L\'email d\'invitation\n\n' +
            'Un email sera envoyé automatiquement !\n\n' +
            'Accédez à [Créer un événement] ou [la liste de vos événements] pour modifier un existant.',
            [MENU_OPTION],
            [
              { text: 'Créer un événement', href: '/admin/events/add' },
              { text: 'la liste de vos événements', href: '/admin/events' },
            ],
          );
          break;

        // ── FILTERS ──
        case 'filters':
          addBotMessage('Aide sur les filtres de quelle section ?', [
            { label: 'Filtres carte', emoji: '🗺️', actionId: 'filters_map' },
            { label: 'Filtres agenda', emoji: '📅', actionId: 'filters_agenda' },
            { label: 'Lancer le tutoriel', emoji: '▶️', actionId: 'launch_filter_tutorial' },
            MENU_OPTION,
          ]);
          break;

        case 'filters_map':
          addUserMessage('Filtres de la carte');
          addBotMessage(
            '🗺️ Sur [la carte], vous pouvez filtrer les résultats :\n\n' +
            '• 🔍 Recherche : nom d\'un acteur\n' +
            '• 📍 Lieu et rayon : adresse / ville, puis curseur de distance (ex: 20 km)\n' +
            '• ❤️ Mes favoris : afficher uniquement vos acteurs/événements favoris\n' +
            '• 💾 Filtres sauvegardés : retrouvez les filtres que vous avez enregistrés\n' +
            '• 🏷️ Catégories : sélectionnez les thématiques qui vous intéressent\n' +
            '• 🤝 Collectif : filtrez par réseau / collectif\n\n' +
            '💡 Vous pouvez sauvegarder un filtre puis activer les notifications pour être averti des nouveautés.',
            [
              { label: 'Lancer le tutoriel', emoji: '▶️', actionId: 'launch_filter_tutorial' },
              { label: 'Filtres agenda', emoji: '📅', actionId: 'filters_agenda' },
              MENU_OPTION,
            ],
            [{ text: 'la carte', href: '/carte' }],
          );
          break;

        case 'filters_agenda':
          addUserMessage('Filtres de l\'agenda');
          addBotMessage(
            '📅 Sur [l\'agenda], plusieurs filtres sont disponibles :\n\n' +
            '• 📆 Date : sélectionnez une date de début pour voir les événements à venir\n' +
            '• 📍 Lieu et rayon : tapez une ville et réglez le rayon en km\n' +
            '• ❤️ Mes favoris : afficher uniquement vos événements favoris\n' +
            '• 💾 Filtres sauvegardés + 🔔 notifications\n' +
            '• 🏷️ Catégories : filtrez par thématique\n' +
            '• 🔄 Récurrents : affichez les événements qui se répètent\n\n' +
            '💡 Cliquez sur « Lancer le tutoriel » pour une démo guidée.',
            [
              { label: 'Lancer le tutoriel', emoji: '▶️', actionId: 'launch_filter_tutorial' },
              { label: 'Filtres carte', emoji: '🗺️', actionId: 'filters_map' },
              MENU_OPTION,
            ],
            [{ text: 'l\'agenda', href: '/agenda' }],
          );
          break;

        case 'launch_filter_tutorial':
          addUserMessage('Lancer le tutoriel des filtres');
          if (router.pathname !== '/carte' && router.pathname !== '/agenda') {
            addBotMessage(
              '👉 Le tutoriel s\'ouvre sur [la carte] ou [l\'agenda]. Cliquez sur l\'un des deux pour le lancer.',
              [MENU_OPTION],
              [
                { text: 'la carte', href: '/carte?openTutorial=1' },
                { text: 'l\'agenda', href: '/agenda?openTutorial=1' },
              ],
            );
          } else {
            window.dispatchEvent(new Event('ouaaa:open-filters'));
            window.dispatchEvent(new Event('ouaaa:open-filter-tutorial'));
            setIsOpen(false);
          }
          break;

        // ── RECIPES ──
        case 'recipes':
          addBotMessage('🥗 Que souhaitez-vous faire avec les recettes ?', [
            { label: 'Voir les recettes', emoji: '📖', actionId: 'search_recipe' },
            { label: 'Créer une recette', emoji: '➕', actionId: 'create_recipe' },
            { label: 'Modifier une recette', emoji: '✏️', actionId: 'modify_recipe' },
            { label: 'Impact carbone', emoji: '🌍', actionId: 'recipe_carbon' },
            MENU_OPTION,
          ]);
          break;

        case 'recipe_carbon':
          addUserMessage('Impact carbone des recettes');
          addBotMessage(
            '🌍 Chaque recette sur OUAAA! affiche son empreinte carbone :\n\n' +
            '• L\'impact est calculé automatiquement à partir des ingrédients que vous ajoutez\n' +
            '• Chaque ingrédient est relié à la base nutritionnelle (BaseAlim) qui contient les coefficients carbone\n' +
            '• Le calcul prend en compte la quantité et l\'unité (g, kg, L, ml...)\n' +
            '• L\'impact total est exprimé en kg de CO₂ équivalent\n\n' +
            '💡 Astuce : privilégiez les ingrédients locaux et de saison pour réduire l\'empreinte !\n\n' +
            'Consultez [les recettes] pour comparer les impacts.',
            [{ label: 'Créer une recette', emoji: '🥗', actionId: 'create_recipe' }, MENU_OPTION],
            [{ text: 'les recettes', href: '/recettes' }],
          );
          break;

        // ── ARTICLES ──
        case 'articles':
          addBotMessage('📰 Que souhaitez-vous faire avec les articles ?', [
            { label: 'Voir les articles', emoji: '📖', actionId: 'search_article' },
            { label: 'Créer un article', emoji: '➕', actionId: 'create_article' },
            { label: 'Modifier un article', emoji: '✏️', actionId: 'modify_article' },
            MENU_OPTION,
          ]);
          break;

        // ── NOTIFICATIONS ──
        case 'notifications':
          addUserMessage('Notifications');
          addBotMessage(
            '🔔 Les notifications OUAAA! vous permettent de rester informé :\n\n' +
            '• 📬 Info-lettre acteur : abonnez-vous aux acteurs qui vous intéressent depuis leur page pour recevoir leurs actualités\n' +
            '• 📅 Rappels événements : recevez un rappel avant les événements auxquels vous participez\n' +
            '• 🤝 Co-organisation : soyez notifié quand un acteur vous invite à co-organiser un événement\n' +
            '• ✅ Validation : recevez une notification quand votre page acteur est validée\n\n' +
            'Vous pouvez gérer vos préférences de notification depuis votre compte.\n' +
            'Pour vous désabonner, cliquez sur le lien en bas de chaque email de notification.',
            [{ label: 'Mon compte', emoji: '👤', actionId: 'account' }, MENU_OPTION],
          );
          break;

        // ── BUG & IMPROVEMENT ──
        case 'bug':
          addBotMessage(
            '🐛 Décrivez le problème rencontré :\n\n' +
            '• Sur quelle page étiez-vous ?\n' +
            '• Quelle action avez-vous effectuée ?\n' +
            '• Que s\'est-il passé vs ce que vous attendiez ?\n\n' +
            'Les informations de votre session seront automatiquement ajoutées au rapport.',
          );
          setInputMode('bug_description');
          break;

        case 'improvement':
          addBotMessage(
            '💡 Décrivez l\'amélioration souhaitée :\n\n' +
            '• Quelle fonctionnalité est concernée ?\n' +
            '• Comment devrait-elle fonctionner ?\n' +
            '• En quoi cela serait-il utile ?\n\n' +
            'Les informations de votre session seront automatiquement ajoutées.',
          );
          setInputMode('improvement_description');
          break;

        case 'send_report':
          addUserMessage('Envoyer le rapport');
          router.push('/contact?from=chatbot');
          addBotMessage(
            '📨 Le [formulaire de contact] a été pré-rempli avec votre rapport.\n\n' +
            'Vérifiez les informations, complétez le captcha si nécessaire, et cliquez sur "Envoyer".',
            [MENU_OPTION],
            [{ text: 'formulaire de contact', href: '/contact?from=chatbot' }],
          );
          break;

        // ── CONTACT ──
        case 'contact':
          addUserMessage('Contacter le support');
          addBotMessage(
            '💬 Envoyez un message à l\'équipe OUAAA! via le [formulaire de contact].\n\nVous pouvez aussi signaler un bug ou proposer une amélioration.',
            [
              { label: 'Signaler un bug', emoji: '🐛', actionId: 'bug' },
              { label: 'Proposer une amélioration', emoji: '💡', actionId: 'improvement' },
              MENU_OPTION,
            ],
            [{ text: 'formulaire de contact', href: '/contact' }],
          );
          break;

        // ── TIPS ──
        case 'tips_actor':
          addBotMessage(
            '💡 Conseils pour une bonne page acteur :\n\n' +
            '• 📸 Ajoutez un logo et une photo de couverture\n' +
            '• 📝 Rédigez une description claire et engageante\n' +
            '• 📍 Indiquez votre adresse pour la carte\n' +
            '• 🌐 Ajoutez vos liens web et réseaux sociaux\n' +
            '• 🏷️ Choisissez les bonnes catégories',
            [MENU_OPTION],
          );
          break;

        case 'tips_event':
          addBotMessage(
            '💡 Conseils pour un bon événement :\n\n' +
            '• 📸 Ajoutez une image attractive\n' +
            '• 📝 Décrivez clairement le programme\n' +
            '• 📍 Indiquez le lieu précis\n' +
            '• 📅 Vérifiez les horaires\n' +
            '• 🏷️ Choisissez les catégories appropriées\n' +
            '• 🤝 Invitez des co-organisateurs si pertinent',
            [MENU_OPTION],
          );
          break;

        // ── FAQ ──
        case 'faq':
          addBotMessage('❓ Quelle question vous intéresse ?', [
            { label: 'Créer un compte', emoji: '👤', actionId: 'faq_create_account' },
            { label: 'À quoi sert un compte ?', emoji: '🎯', actionId: 'faq_account_purpose' },
            { label: 'Qu\'est-ce qu\'un acteur ?', emoji: '🌱', actionId: 'faq_what_actor' },
            { label: 'Remplir ma page acteur', emoji: '📝', actionId: 'faq_fill_actor' },
            { label: 'Plusieurs pages acteur ?', emoji: '📄', actionId: 'faq_multiple_actors' },
            { label: 'Co-administrer un acteur', emoji: '👥', actionId: 'faq_coadmin' },
            { label: 'Remplir mon agenda', emoji: '📅', actionId: 'faq_fill_agenda' },
            { label: 'Supprimer', emoji: '🗑️', actionId: 'faq_delete' },
            { label: 'Mes données', emoji: '🔒', actionId: 'faq_data' },
            { label: 'C\'est où l\'Aunis ?', emoji: '📍', actionId: 'faq_aunis' },
            { label: 'Qui peut s\'inscrire ?', emoji: '🚫', actionId: 'faq_restrictions' },
            { label: 'Voir toute la FAQ', emoji: '📖', actionId: 'faq_full' },
            MENU_OPTION,
          ]);
          break;

        case 'faq_create_account':
          addUserMessage('Comment créer un compte ?');
          addBotMessage(
            '👤 Pour créer votre compte :\n\n' +
            '1. Cliquez sur l\'icône personne en haut à droite\n' +
            '2. Cliquez sur "Créer un compte" ou allez sur [la page d\'inscription]\n' +
            '3. Le mot de passe doit contenir : au moins 8 caractères, une majuscule, une minuscule, un chiffre et un caractère spécial (sans espace)\n' +
            '4. Un email de confirmation vous sera envoyé\n' +
            '5. C\'est parti !',
            [
              { label: 'Créer un compte', emoji: '📝', actionId: 'create_account' },
              { label: 'Autre question', emoji: '❓', actionId: 'faq' },
              MENU_OPTION,
            ],
            [{ text: 'la page d\'inscription', href: '/signup' }],
          );
          break;

        case 'faq_account_purpose':
          addUserMessage('À quoi sert un compte ?');
          addBotMessage(
            '🎯 Avec un compte personnel vous pouvez :\n\n' +
            '• Créer une ou plusieurs pages acteurs\n' +
            '• Ajouter des acteurs en favoris\n' +
            '• Participer à des actions de l\'agenda\n' +
            '• Souscrire à l\'info-lettre d\'un acteur\n' +
            '• Indiquer votre envie de devenir bénévole\n\n' +
            'Votre compte est personnel et invisible sur le site.',
            [
              { label: 'Créer un compte', emoji: '📝', actionId: 'create_account' },
              { label: 'Autre question', emoji: '❓', actionId: 'faq' },
              MENU_OPTION,
            ],
          );
          break;

        case 'faq_what_actor':
          addUserMessage('Qu\'est-ce qu\'un acteur ?');
          addBotMessage(
            '🌱 Un acteur de la transition, c\'est :\n\n' +
            'Particuliers, associations, entreprises, coopératives ou services publics qui agissent pour améliorer la société, dans 4 thématiques :\n\n' +
            '🏡 Vivre mieux : éducation, culture, santé, alimentation\n' +
            '🤝 Vivre ensemble : solidarité, économie, citoyenneté\n' +
            '🏭 Produire mieux : agriculture, industrie, recyclage, énergie\n' +
            '🌍 Vivre avec la planète : air, eau, sol, biodiversité',
            [
              { label: 'Qui peut s\'inscrire ?', emoji: '🚫', actionId: 'faq_restrictions' },
              { label: 'Créer une page acteur', emoji: '🏢', actionId: 'create_actor' },
              { label: 'Autre question', emoji: '❓', actionId: 'faq' },
              MENU_OPTION,
            ],
          );
          break;

        case 'faq_fill_actor':
          addUserMessage('Comment remplir ma page acteur ?');
          addBotMessage(
            '📝 Pour remplir votre page acteur :\n\n' +
            '1. Créez un compte personnel\n' +
            '2. Rendez-vous sur [Ajouter un acteur]\n' +
            '3. Confirmez le respect de la charte OUAAA!\n' +
            '4. Remplissez les champs (les * sont obligatoires)\n\n' +
            '💡 Des infobulles (icône ℹ️) vous guident à chaque étape.\nPlus votre fiche est complète, mieux les internautes vous trouveront !',
            [
              { label: 'Conseils page acteur', emoji: '💡', actionId: 'tips_actor' },
              { label: 'Autre question', emoji: '❓', actionId: 'faq' },
              MENU_OPTION,
            ],
            [{ text: 'Ajouter un acteur', href: '/addactor' }],
          );
          break;

        case 'faq_multiple_actors':
          addUserMessage('Plusieurs pages acteur ?');
          addBotMessage(
            '📄 Oui ! Votre compte personnel vous permet de créer autant de pages acteur que vous le souhaitez.\n\n' +
            'Votre compte est invisible sur le site, il sert uniquement à administrer vos pages.',
            [
              { label: 'Créer une page acteur', emoji: '🏢', actionId: 'create_actor' },
              { label: 'Co-administrer', emoji: '👥', actionId: 'faq_coadmin' },
              { label: 'Autre question', emoji: '❓', actionId: 'faq' },
              MENU_OPTION,
            ],
          );
          break;

        case 'faq_coadmin':
          addUserMessage('Co-administrer une page acteur');
          addBotMessage(
            '👥 Pour ajouter un co-administrateur :\n\n' +
            '1. Allez en fin de page dans l\'éditeur de votre acteur\n' +
            '2. Trouvez la rubrique "Référent associé à l\'acteur"\n' +
            '3. Tapez le prénom-nom du co-administrateur\n\n' +
            '⚠️ La personne doit déjà avoir un compte OUAAA! pour être ajoutée.',
            [
              { label: 'Modifier ma page acteur', emoji: '✏️', actionId: 'modify_actor' },
              { label: 'Autre question', emoji: '❓', actionId: 'faq' },
              MENU_OPTION,
            ],
          );
          break;

        case 'faq_fill_agenda':
          addUserMessage('Comment remplir mon agenda ?');
          addBotMessage(
            '📅 Pour ajouter des événements à votre agenda :\n\n' +
            '1. Connectez-vous à votre compte\n' +
            '2. Vous devez avoir créé une page acteur\n' +
            '3. Sur votre page acteur, au niveau du calendrier, cliquez sur "Ajouter un événement"\n' +
            '4. Des infobulles (icône ℹ️) vous guident à chaque étape\n' +
            '5. Remplissez un maximum d\'infos pour donner envie !\n\n' +
            'Vous pouvez aussi passer par [Créer un événement].',
            [
              { label: 'Créer un événement', emoji: '📅', actionId: 'create_event' },
              { label: 'Conseils événement', emoji: '💡', actionId: 'tips_event' },
              { label: 'Autre question', emoji: '❓', actionId: 'faq' },
              MENU_OPTION,
            ],
            [{ text: 'Créer un événement', href: '/admin/events/add' }],
          );
          break;

        case 'faq_delete':
          addBotMessage('Que souhaitez-vous supprimer ?', [
            { label: 'Une page acteur', emoji: '🏢', actionId: 'faq_delete_actor' },
            { label: 'Un événement', emoji: '📅', actionId: 'faq_delete_event' },
            { label: 'Mon compte', emoji: '👤', actionId: 'faq_delete_account' },
            { label: 'Autre question', emoji: '❓', actionId: 'faq' },
            MENU_OPTION,
          ]);
          break;

        case 'faq_delete_actor':
          addUserMessage('Supprimer une page acteur');
          addBotMessage(
            '🗑️ Pour supprimer une page acteur :\n\n' +
            'Le bouton se trouve en fin de page dans l\'éditeur.\n\n' +
            '⚠️ Attention : cette action est irréversible après confirmation. Toutes les données associées (actions, événements) seront aussi supprimées.',
            [
              { label: 'Modifier ma page acteur', emoji: '✏️', actionId: 'modify_actor' },
              { label: 'Autre question', emoji: '❓', actionId: 'faq' },
              MENU_OPTION,
            ],
          );
          break;

        case 'faq_delete_event':
          addUserMessage('Supprimer un événement');
          addBotMessage(
            '🗑️ Pour supprimer un événement :\n\n' +
            'En fin du formulaire d\'édition, cliquez sur "Supprimer l\'événement".\nUne fenêtre de confirmation apparaîtra.\n\n' +
            '⚠️ Attention : la suppression est définitive.',
            [
              { label: 'Modifier un événement', emoji: '✏️', actionId: 'modify_event' },
              { label: 'Autre question', emoji: '❓', actionId: 'faq' },
              MENU_OPTION,
            ],
          );
          break;

        case 'faq_delete_account':
          addUserMessage('Supprimer mon compte');
          addBotMessage(
            '🗑️ Pour supprimer votre compte :\n\n' +
            '1. Allez dans "Mon compte" (icône en haut à droite)\n' +
            '2. Dans "Infos personnelles", cliquez sur "Supprimer mon compte"\n' +
            '3. Confirmez dans le popup\n\n' +
            '⚠️ Avant de supprimer votre compte :\n' +
            '• Assurez-vous qu\'un autre admin gère vos pages acteur\n' +
            '• Supprimez vos pages si elles doivent disparaître\n\n' +
            'Cette action est irréversible.',
            [
              { label: 'Autre question', emoji: '❓', actionId: 'faq' },
              MENU_OPTION,
            ],
          );
          break;

        case 'faq_data':
          addUserMessage('Que deviennent mes données ?');
          addBotMessage(
            '🔒 Vos données ne seront jamais diffusées à des tiers.\n\nElles servent uniquement à :\n\n' +
            '1. 📬 Transmettre vos coordonnées à un acteur quand vous mettez en favori, participez à une action ou devenez bénévole\n' +
            '2. 📊 Nos statistiques internes (anonymes) pour améliorer le site',
            [
              { label: 'Autre question', emoji: '❓', actionId: 'faq' },
              MENU_OPTION,
            ],
          );
          break;

        case 'faq_aunis':
          addUserMessage('C\'est où l\'Aunis ?');
          addBotMessage(
            '📍 Pour OUAAA!, l\'Aunis regroupe :\n\n' +
            '• Communauté de communes Aunis Atlantique\n' +
            '• Communauté de communes Aunis Sud\n' +
            '• Communauté de communes de l\'Île de Ré\n' +
            '• Agglomération de La Rochelle',
            [
              { label: 'Autre question', emoji: '❓', actionId: 'faq' },
              MENU_OPTION,
            ],
          );
          break;

        case 'faq_restrictions':
          addUserMessage('Qui peut s\'inscrire ?');
          addBotMessage(
            '🚫 Ne peuvent pas s\'inscrire sur OUAAA! :\n\n' +
            '• Structures nationales/internationales sans succursale en Aunis\n' +
            '• Prestations de soin/coaching impliquant ascendant moral\n' +
            '• Partis politiques et mouvements prosélytes\n' +
            '• Acteurs ayant des pratiques discriminatoires',
            [
              { label: 'Qu\'est-ce qu\'un acteur ?', emoji: '🌱', actionId: 'faq_what_actor' },
              { label: 'Autre question', emoji: '❓', actionId: 'faq' },
              MENU_OPTION,
            ],
          );
          break;

        case 'faq_full':
          addUserMessage('Voir toute la FAQ');
          addBotMessage(
            '📖 Retrouvez l\'ensemble des questions-réponses sur la [page FAQ].',
            [
              { label: 'Autre question', emoji: '❓', actionId: 'faq' },
              MENU_OPTION,
            ],
            [{ text: 'page FAQ', href: '/faq' }],
          );
          break;

        case 'hide_chatbot':
          addUserMessage('Masquer l\'assistant');
          hideChatbot();
          break;

        default:
          break;
      }
    },
    [user, router, addBotMessage, addUserMessage, showMainMenu, hideChatbot],
  );

  // ── Text input submission (bug/improvement) ──

  const handleInputSubmit = useCallback(() => {
    if (!inputValue.trim()) return;

    const text = inputValue.trim();
    addUserMessage(text);
    setInputValue('');

    if (inputMode === 'bug_description' || inputMode === 'improvement_description') {
      const sessionInfo = getSessionInfo();
      const isBug = inputMode === 'bug_description';

      const reportMessage =
        `--- ${isBug ? 'Rapport de bug' : 'Suggestion d\'amélioration'} ---\n\n` +
        `${text}\n\n` +
        `--- Informations de session ---\n` +
        `Page : ${sessionInfo.pageActuelle}\n` +
        `Écran : ${sessionInfo.ecran}\n` +
        `Navigateur : ${sessionInfo.navigateur}\n` +
        `Utilisateur : ${sessionInfo.utilisateur}\n` +
        `Date : ${sessionInfo.date}\n\n` +
        `Historique de navigation :\n${sessionInfo.historique}`;

      try {
        localStorage.setItem(
          'chatbot_report',
          JSON.stringify({
            category: 'improvementbug',
            object: isBug
              ? `[Bug] ${text.substring(0, 80)}`
              : `[Amélioration] ${text.substring(0, 80)}`,
            message: reportMessage,
            name: user ? `${user.surname} ${user.lastname}` : '',
            email: user?.email || '',
          }),
        );
      } catch {
        // localStorage may be unavailable
      }

      addBotMessage(
        '✅ Merci ! Informations de session collectées :\n\n' +
        `📍 Page : ${sessionInfo.pageActuelle}\n` +
        `🖥️ Écran : ${sessionInfo.ecran}\n` +
        `👤 ${sessionInfo.utilisateur}\n` +
        `📜 ${navigationHistory.current.length} pages visitées\n\n` +
        'Souhaitez-vous envoyer ce rapport au support ?',
        [
          { label: 'Envoyer le rapport', emoji: '📨', actionId: 'send_report' },
          MENU_OPTION,
        ],
      );
      setInputMode(null);
    }
  }, [inputValue, inputMode, addUserMessage, addBotMessage, getSessionInfo, user]);

  // ── Toggle open/close ──

  const toggle = useCallback(() => {
    if (!isOpen) {
      setIsOpen(true);
      setShowPulse(false);
      if (!initialized.current) {
        initialized.current = true;
        addBotMessage('Bonjour ! 👋 Je suis l\'assistant OUAAA!');
        const ctx = getContextualSuggestion(router.asPath);
        if (ctx) {
          addBotMessage(ctx.greeting, ctx.options);
        } else {
          showMainMenu();
        }
      }
    } else {
      setIsOpen(false);
    }
  }, [isOpen, router.asPath, addBotMessage, showMainMenu]);

  // ── Drag handlers ──

  const handleFabPointerDown = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragStateRef.current = { startX: e.clientX, startY: e.clientY, isDragging: false, isOverDismiss: false };
  }, []);

  const handleFabPointerMove = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    if (!dragStateRef.current) return;
    e.preventDefault();
    const dx = Math.abs(e.clientX - dragStateRef.current.startX);
    const dy = Math.abs(e.clientY - dragStateRef.current.startY);
    if (!dragStateRef.current.isDragging && (dx > 8 || dy > 8)) {
      dragStateRef.current.isDragging = true;
      setIsDragging(true);
    }
    if (dragStateRef.current.isDragging) {
      setDragPos({ x: e.clientX, y: e.clientY });
      const centerX = window.innerWidth / 2;
      const centerY = window.innerHeight / 2;
      const dist = Math.sqrt((e.clientX - centerX) ** 2 + (e.clientY - centerY) ** 2);
      const over = dist < 60;
      dragStateRef.current.isOverDismiss = over;
      setIsOverDismiss(over);
    }
  }, []);

  const handleFabPointerUp = useCallback(() => {
    if (!dragStateRef.current) return;
    const wasDrag = dragStateRef.current.isDragging;
    const overDismiss = dragStateRef.current.isOverDismiss;
    dragStateRef.current = null;
    setIsDragging(false);
    setDragPos(null);
    setIsOverDismiss(false);
    if (wasDrag && overDismiss) {
      hideChatbot();
      return;
    }
    if (!wasDrag) {
      toggle();
    }
  }, [hideChatbot, toggle]);

  // ── Only show options for the last bot message ──

  const lastBotMsgWithOptionsId = messages
    .filter((m) => m.from === 'bot' && m.options?.length)
    .at(-1)?.id;

  // ── Dimensions ──

  const panelWidth = isMobile ? '100%' : 380;
  const panelHeight = isMobile ? '100%' : 520;

  return (
    <Box
      sx={{
        position: 'fixed',
        zIndex: 1250,
        ...(isMobile && isOpen
          ? { top: 0, left: 0, right: 0, bottom: 0 }
          : { bottom: isAdminPage ? 100 : 90, right: 24 }),
      }}
    >
      {/* ── Chat panel ── */}
      <Slide direction="up" in={isOpen} mountOnEnter unmountOnExit>
        <Paper
          elevation={8}
          sx={{
            width: panelWidth,
            height: panelHeight,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: isMobile ? 0 : 3,
            overflow: 'hidden',
            mb: isMobile ? 0 : 1,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              px: 2,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
            }}
          >
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
              💬 Assistant OUAAA!
            </Typography>
            <IconButton
              onClick={toggle}
              sx={{
                color: 'white',
                bgcolor: 'rgba(255,255,255,0.15)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
              }}
            >
              <CloseIcon sx={{ color: '#fff' }} />
            </IconButton>
          </Box>

          {/* Messages area */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              px: 2,
              py: 1.5,
              bgcolor: '#f5f5f5',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            {messages.map((msg) => (
              <Box
                key={msg.id}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: msg.from === 'bot' ? 'flex-start' : 'flex-end',
                }}
              >
                {/* Bubble */}
                <Box
                  sx={{
                    maxWidth: '85%',
                    bgcolor: msg.from === 'bot' ? 'white' : 'primary.main',
                    color: msg.from === 'bot' ? 'text.primary' : 'white',
                    ...(msg.from === 'user'
                      ? {
                        '&, & *': {
                          color: '#fff !important',
                          WebkitTextFillColor: '#fff',
                        },
                      }
                      : {}),
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                    boxShadow: 1,
                    whiteSpace: 'pre-line',
                    fontSize: '0.875rem',
                    lineHeight: 1.6,
                  }}
                >
                  <BotMessageContent text={msg.text} links={msg.from === 'bot' ? msg.links : undefined} />
                </Box>

                {/* Options (only for last bot message with options) */}
                {msg.options && msg.id === lastBotMsgWithOptionsId && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 0.5,
                      mt: 1,
                      maxWidth: '95%',
                    }}
                  >
                    {msg.options.map((opt, i) => (
                      <Button
                        key={`${opt.actionId}-${i}`}
                        size="small"
                        variant="outlined"
                        onClick={() => handleAction(opt.actionId)}
                        sx={{
                          textTransform: 'none',
                          borderRadius: 4,
                          fontSize: '0.75rem',
                          py: 0.5,
                          px: 1.5,
                          borderColor: theme.palette.primary.light,
                          color: theme.palette.primary.main,
                          bgcolor: 'white',
                          '&, & *': {
                            color: 'inherit',
                          },
                          '&:hover, &:active, &:focus, &:focus-visible, &.Mui-focusVisible': {
                            bgcolor: theme.palette.primary.main,
                            color: '#fff !important',
                            WebkitTextFillColor: '#fff',
                            borderColor: theme.palette.primary.main,
                            '&, & *': {
                              color: '#fff !important',
                              WebkitTextFillColor: '#fff',
                            },
                          },
                        }}
                      >
                        {opt.emoji && <span style={{ marginRight: 4 }}>{opt.emoji}</span>}
                        {opt.label}
                      </Button>
                    ))}
                  </Box>
                )}
              </Box>
            ))}
            <div ref={messagesEndRef} />
          </Box>

          {/* Input area (bug/improvement description) */}
          {inputMode && (
            <Box
              sx={{
                borderTop: '1px solid',
                borderColor: 'divider',
                p: 1.5,
                bgcolor: 'white',
                display: 'flex',
                gap: 1,
                flexShrink: 0,
              }}
            >
              <InputBase
                fullWidth
                multiline
                maxRows={3}
                placeholder={
                  inputMode === 'bug_description'
                    ? 'Décrivez le bug...'
                    : 'Décrivez l\'amélioration...'
                }
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleInputSubmit();
                  }
                }}
                sx={{
                  bgcolor: '#f5f5f5',
                  borderRadius: 2,
                  px: 1.5,
                  py: 0.75,
                  fontSize: '0.875rem',
                }}
              />
              <IconButton color="primary" onClick={handleInputSubmit} disabled={!inputValue.trim()}>
                <SendIcon />
              </IconButton>
            </Box>
          )}
        </Paper>
      </Slide>

      {/* ── Drag dismiss overlay ── */}
      {isDragging && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              width: 100,
              height: 100,
              borderRadius: '50%',
              bgcolor: isOverDismiss ? 'error.main' : 'rgba(255,255,255,0.9)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s',
              transform: isOverDismiss ? 'scale(1.3)' : 'scale(1)',
              boxShadow: 3,
            }}
          >
            <CloseIcon sx={{ fontSize: 40, color: isOverDismiss ? '#fff' : 'text.secondary' }} />
          </Box>
        </Box>
      )}

      {/* ── FAB button (draggable) ── */}
      {!isHidden && !(isOpen && isMobile) && (
        <Fab
          color="primary"
          onPointerDown={handleFabPointerDown}
          onPointerMove={handleFabPointerMove}
          onPointerUp={handleFabPointerUp}
          aria-label="Assistant OUAAA"
          sx={{
            touchAction: 'none',
            userSelect: 'none',
            ...(isDragging && dragPos
              ? {
                position: 'fixed',
                left: dragPos.x - 28,
                top: dragPos.y - 28,
                bottom: 'auto',
                right: 'auto',
                zIndex: 2,
                transition: 'none',
                transform: isOverDismiss ? 'scale(0.8)' : 'scale(1.1)',
              }
              : {
                position: isMobile ? 'fixed' : 'relative',
                bottom: isMobile ? (isAdminPage ? 100 : 86) : 'auto',
                right: isMobile ? 20 : 'auto',
              }),
            display: isOpen && !isMobile ? 'none' : 'flex',
            boxShadow: isDragging ? 12 : 4,
            animation: !isDragging && showPulse ? 'chatbot-pulse 2s infinite' : 'none',
            '@keyframes chatbot-pulse': {
              '0%': { boxShadow: '0 0 0 0 rgba(44, 54, 126, 0.4)' },
              '70%': { boxShadow: '0 0 0 14px rgba(44, 54, 126, 0)' },
              '100%': { boxShadow: '0 0 0 0 rgba(44, 54, 126, 0)' },
            },
          }}
        >
          <HelpOutlineIcon />
        </Fab>
      )}

      {/* ── Hide notification ── */}
      <Snackbar
        open={showHideSnackbar}
        autoHideDuration={8000}
        onClose={() => setShowHideSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowHideSnackbar(false)}
          severity="info"
          variant="filled"
          sx={{ width: '100%' }}
        >
          Assistant masqué.{' '}
          {user
            ? <>Vous pouvez le réactiver dans <strong>Mes filtres &amp; notifications</strong> ou{' '}<MuiLink component="button" onClick={() => { showChatbot(); setShowHideSnackbar(false); }} sx={{ color: '#fff', textDecoration: 'underline', cursor: 'pointer', verticalAlign: 'baseline', fontSize: 'inherit', fontWeight: 'bold' }}>cliquez ici</MuiLink>.</>
            : <><MuiLink component="button" onClick={() => { showChatbot(); setShowHideSnackbar(false); }} sx={{ color: '#fff', textDecoration: 'underline', cursor: 'pointer', verticalAlign: 'baseline', fontSize: 'inherit', fontWeight: 'bold' }}>Cliquez ici</MuiLink> pour le réafficher.</>}
        </Alert>
      </Snackbar>
    </Box>
  );
}
